import { authOnce } from '@app/authentification/auth-once';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { isMainAccountUser } from '@app/authentification/user-identity';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { map, Observable, of, switchMap, take } from 'rxjs';

export abstract class Dao {
  constructor(
    protected httpApi: HttpApiService,
    protected authentificationService: AuthentificationService,
  ) {
    this.authentificationService.authentication.subscribe(event =>
      this.userChanged(event),
    );
  }

  getAuthentified() {
    return this.authentificationService.authentication;
  }

  userChanged(event) {
    this.onUserChange(event);
  }

  getAuthentifiedMainUserOrMainUserFromAuthentifiedSpouse(): Observable<UserAuthentified> {
    return this.getAuthentified().pipe(
      switchMap(user =>
        isMainAccountUser(user)
          ? of(user)
          : this.httpApi
              .get(`/user/${user.accountMainUserId}`)
              .pipe(map((res: any) => res.data)),
      ),
    );
  }

  /**
   * Instantané one-shot de l'utilisateur authentifié : émet une fois, puis
   * complète. Pour les chaînes d'écriture. Pour l'affichage réactif,
   * conserver getAuthentified().
   */
  authOnce$(): Observable<UserAuthentified> {
    return authOnce(this.getAuthentified());
  }

  /**
   * Résolution one-shot de l'utilisateur principal du compte (gère le
   * conjoint) : émet une fois, puis complète.
   */
  protected mainUserOnce$(): Observable<UserAuthentified> {
    return this.authOnce$().pipe(
      switchMap(user =>
        isMainAccountUser(user)
          ? of(user)
          : this.httpApi
              .get(`/user/${user.accountMainUserId}`)
              .pipe(map((res: any) => res.data)),
      ),
      take(1),
    );
  }

  /**
   * Garantit une émission unique + la complétion pour les
   * chaînes d'écriture, même si run() retourne un flux vif (non terminant).
   */
  protected command<R>(
    run: (user: UserAuthentified) => Observable<R>,
  ): Observable<R> {
    return this.authOnce$().pipe(
      switchMap(user => run(user)),
      take(1),
    );
  }

  /**
   * Comme command(), en résolvant l'utilisateur principal du compte (gère le
   * conjoint).
   */
  protected commandAsMainUser<R>(
    run: (user: UserAuthentified) => Observable<R>,
  ): Observable<R> {
    return this.mainUserOnce$().pipe(
      switchMap(user => run(user)),
      take(1),
    );
  }

  /* state */
  abstract onUserChange(event: any);
}
